import {CONFIG} from '../../appConfig';
import moment from 'moment';

const Moment = moment;


/**
 * @private
 * @name setLanguageOnHTML
 * @desc Set language attribute on document
 * @param {String} language
 */
function setLanguageOnHTML(language) {
  document.documentElement.setAttribute('lang', language);
}


/**
 * @private
 * @name setDateTimeLocale
 * @desc Setup locale by format
 * @param {String} language
 * @param {Object} format
 */
function setDateTimeLocale(language, format) {
  Moment[Moment.locales().indexOf(language) !== -1 ? 'updateLocale' : 'locale'](language, format);
}


/**
 * @name getDateTimeLocale
 * @desc Return date time locale info
 * @return {Object}
 * @memberof utils
 */
export function getDateTimeLocale() {
  return Moment().localeData();
}


/**
 * @name setDateTimeConfig
 * @desc Site wide date time config setup
 * @param {Object} [opts] {language: String, format: Object}
 * @memberof utils
 * @example
 * See full documentation for Moment https://momentjs.com/docs/#/i18n/changing-locale/
 * const language = 'en';
 * const format = {
 *   months : 'janvier_février_mars_avril_mai_juin_juillet_août_septembre_octobre_novembre_décembre'.split('_'),
 *   monthsShort : 'janv._févr._mars_avr._mai_juin_juil._août_sept._oct._nov._déc.'.split('_'),
 *   monthsParseExact : true,
 *   weekdays : 'dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi'.split('_'),
 *   weekdaysShort : 'dim._lun._mar._mer._jeu._ven._sam.'.split('_'),
 *   weekdaysMin : 'Di_Lu_Ma_Me_Je_Ve_Sa'.split('_'),
 *   weekdaysParseExact : true,
 *   longDateFormat : {
 *     LT : 'HH:mm',
 *     LTS : 'HH:mm:ss',
 *     L : 'DD/MM/YYYY',
 *     LL : 'D MMMM YYYY',
 *     LLL : 'D MMMM YYYY HH:mm',
 *     LLLL : 'dddd D MMMM YYYY HH:mm'
 *   },
 *   calendar : {
 *     sameDay : '[Aujourd’hui à] LT',
 *     nextDay : '[Demain à] LT',
 *     nextWeek : 'dddd [à] LT',
 *     lastDay : '[Hier à] LT',
 *     lastWeek : 'dddd [dernier à] LT',
 *     sameElse : 'L'
 *   },
 *   relativeTime : {
 *     future : 'dans %s',
 *     past : 'il y a %s',
 *     s : 'quelques secondes',
 *     m : 'une minute',
 *     mm : '%d minutes',
 *     h : 'une heure',
 *     hh : '%d heures',
 *     d : 'un jour',
 *     dd : '%d jours',
 *     M : 'un mois',
 *     MM : '%d mois',
 *     y : 'un an',
 *     yy : '%d ans'
 *   },
 *   dayOfMonthOrdinalParse : /\d{1,2}(er|e)/,
 *   ordinal : function (number) {
 *     return number + (number === 1 ? 'er' : 'e');
 *   },
 *   meridiemParse : /PD|MD/,
 *   isPM : function (input) {
 *     return input.charAt(0) === 'M';
 *   },
 *   // In case the meridiem units are not separated around 12, then implement
 *   // this function (look at locale/id.js for an example).
 *   // meridiemHour : function (hour, meridiem) {
 *   //   return 0-23 hour, given meridiem token and hour 1-12
 *   // },
 *   // meridiem : function (hours, minutes, isLower) {
 *   //   return hours < 12 ? 'PD' : 'MD';
 *   // },
 *   week : {
 *     dow : 1, // Monday is the first day of the week.
 *     doy : 4  // Used to determine first week of the year.
 *   }
 * }
 *
 * setDateTimeConfig({language, format})
 */
export function setDateTimeConfig(opts = {language: CONFIG.LANGUAGE, format: CONFIG.DATE_TIME_FORMAT, shouldSetHTMLLang: CONFIG.SET_HTML_LANG }) {
  const {format, language, shouldSetHTMLLang} = opts;
  if (typeof document !== 'undefined' && language && shouldSetHTMLLang) setLanguageOnHTML(language);
  if (language && format) setDateTimeLocale(language, format);
}

/**
 * @name getDateTimeObjFromDateParts
 * @desc Return date time obj by current or passed date time values
 * @param {Number|String} [year]
 * @param {Number|String} [month] 0 based
 * @param {Number|String} [day]
 * @param {Number|String} [hour]
 * @param {Number|String} [minute]
 * @param {Number|String} [second]
 * @param {Number|String} [millisecond]
 * @return {Object}
 * @memberof utils
 */
export function getDateTimeObjFromDateParts({year, month, day, hour, minute, second, millisecond}) {
  return Moment({year, month, day, hour, minute, second, millisecond});
}


/**
 * @name parseGBODate
 * @desc Parse GBO Date, return object
 * @param {String} date
 * @returns {Object}
 * @memberof utils
 */
export function parseGBODate(date = '') {
  const dateArray = date.split('-');
  return {
    year: dateArray[0],
    month: dateArray[1],
    day: dateArray[2]
  };
}


/**
 * @name getMaskedGBODate
 * @desc Return parsed and formatted date string
 * @param {String} date
 * @return {String}
 * @memberof utils
 */
export function getMaskedGBODate(date) {
  const {year, month, day} = parseGBODate(date);
  return getDateTimeLocale()._longDateFormat.L
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day);
}


/**
 * @name getDateTimeObjFromTs
 * @desc Return a date time obj by timestamp and format
 * @param {String} ts
 * @param {String} format
 * @return {Object}
 * @memberof utils
 */
export function getDateTimeObjFromTs(ts, format = CONFIG.GBO_TIME_FORMAT) {
  return Moment(ts, format);
}


/**
 * @name getCurrentDateTimeObj
 * @desc Returns the current date time obj
 * @returns {Object}
 * @memberof utils
 */
export function getCurrentDateTimeObj() {
  return Moment();
}

/**
 * @name getDateTimeObj
 * @desc Returns the date time obj with passed in params
 * @params {...*} params - indefinite arguments
 * @returns {Object}
 * @memberof utils
 */
export function getDateTimeObj() {
  return Moment.apply(void 0, arguments);
}

/*
 * @name getUTCTime
 * @desc Create a moment object using UTC timezone
 * @params {Object} date
 * @returns {Object}
 * @memberof utils
 */
export function getUTCTime(date) {
  return Moment.utc(date);
}

/**
 * @name getMonthsArray
 * @desc Constructs a month object used for date drop downs, the objects are consisted of a value and a label
 * @returns {Array}
 * @memberof utils
 */
export function getMonthsArray() {
  return Moment.monthsShort().map((month, index) => ({value: index + 1, label: month}));
}


/**
 * @name getGBOTimeStamp
 * @description Gets the gbo time stamp in string format, suitable for submission to services or putting into GMI/Redux
 * Note this expects an input object and does no validation.
 *
 * @param      {Moment}  dateObj  The date object (i.e. Moment)
 * @returns     {string}  The gbo time stamp in CONFIG.GBO_TIME_FORMAT (GBOTimeStamp)
 */
export const getGBOTimeStamp = dateObj => dateObj.format(CONFIG.GBO_TIME_FORMAT);

/**
 * @name setDefaultTime
 * @description Gets the default time, which is high noon.
 *
 * @param      {Moment}  dateObj  The date time object to update
 * @returns     {Moment}  The default time.
 */
export const setDefaultTime = dateObj => dateObj.hour(12).minute(0).second(0);

/**
 * @name getDefaultDateTimeObj
 * @description Gets the default DateTime/TimeStamp for today plus an optional offset
 * @param      {number}  [offset=1?]  The offset to add in days
 * @returns     {Moment}  The default date time.
 */
export const getDefaultDateTimeObj = (offset = 1) => setDefaultTime(Moment().add(offset, 'days'));